import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { CancelTransactionRequest } from '@leanpay/models'
import { CheckoutStep, CheckoutStepViewType } from '@leanpay/models'
import { checkoutService, logoutUser } from '@leanpay/services'
import {
    Button,
    CloseCircleIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTrigger,
    ProgressBar,
    ThinkingFaceImg,
} from '@leanpay/ui'

import { useAppStore } from 'app/app-store'
import { EnhancedProgressBarRom } from 'app/pre-qualify-markets/rom/enhanced-progress-bar-rom/enhanced-progress-bar-rom'
import { EnhancedProgressBarSlo } from 'app/pre-qualify-markets/slo/enhanced-progress-bar-slo/enhanced-progress-bar-slo'
import { isLocalEnvironment, isMarketRom, isMarketSlo, isProdEnvironment } from 'library/constants'
import { useCancelTransaction, useGetSessionInfo } from 'library/hooks/query-hooks'
import { shouldShowProgressBar } from 'library/utils'
import LpLogoImg from 'assets/images/lp_logo.png'

// Map of language names in their native language
const languageNames = {
    // English is always "English"
    en: 'English',
    // Native language names
    sl: 'slovenščina',
    hu: 'magyar',
    ro: 'română',
}

const LanguageSwitcher = () => {
    const { t, i18n } = useTranslation()
    const marketLanguage = isMarketSlo ? 'sl' : 'ro'

    if (isProdEnvironment) {
        return null
    }

    return (
        <div className="flex gap-1">
            <button
                onClick={() => {
                    i18n.changeLanguage('en')
                }}
                className={`inline-flex items-center justify-center rounded-md text-xs font-semibold uppercase w-8 h-8 p-0 border-[1px] border-black transition-colors hover:bg-gray-50 shadow-[0_2px_4px_rgba(0,0,0,0.08)] ${i18n.language === 'en' ? 'bg-lp-peach1' : 'bg-lp-grey1'} text-lp-dark1`}
                aria-label={
                    i18n.language === 'en'
                        ? `${t('common.switchToLanguage', { language: languageNames.en })} (${t('common.currentlySelected')})`
                        : t('common.switchToLanguage', {
                              language: languageNames.en,
                          })
                }
            >
                EN
            </button>
            <button
                onClick={() => {
                    i18n.changeLanguage(marketLanguage)
                }}
                className={`inline-flex items-center justify-center rounded-md text-xs font-semibold uppercase w-8 h-8 p-0 border-[1px] border-black transition-colors hover:bg-gray-50 shadow-[0_2px_4px_rgba(0,0,0,0.08)] ${i18n.language === marketLanguage ? 'bg-lp-peach1' : 'bg-lp-grey1'} text-lp-dark1`}
                aria-label={
                    i18n.language === marketLanguage
                        ? `${t('common.switchToLanguage', {
                              language: languageNames[marketLanguage as keyof typeof languageNames],
                          })} (${t('common.currentlySelected')})`
                        : t('common.switchToLanguage', {
                              language: languageNames[marketLanguage as keyof typeof languageNames],
                          })
                }
            >
                {String(marketLanguage).toUpperCase()}
            </button>
        </div>
    )
}

interface PrequalifyHeaderProps {
    progress: number
    onCancelPress?: () => void
    showCancelIcon?: boolean
    children: any
}

const PrequalifyHeader = ({
    progress,
    onCancelPress,
    showCancelIcon = false,
    children,
}: PrequalifyHeaderProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { vendorData, vendorToken, setIsAuthenticated } = useAppStore()
    const showProgressbar = shouldShowProgressBar()
    const [isOpen, setIsOpen] = useState(false)
    const { data: sessionInfo } = useGetSessionInfo()

    const { mutateAsync: mutateCancelTransaction } = useCancelTransaction()

    const paramsOnEnter = {
        token: vendorToken,
        checkoutStep: CheckoutStep.CANCEL_TRANSACTION,
        checkoutStepViewType: CheckoutStepViewType.OPEN,
        data: { token: vendorToken },
    }
    const paramsOnExit = {
        token: vendorToken,
        checkoutStep: CheckoutStep.CANCEL_TRANSACTION,
        checkoutStepViewType: CheckoutStepViewType.CLOSE,
        data: { token: vendorToken },
    }
    const handleExitCheckoutProcess = async () => {
        void checkoutService.recordStep(paramsOnEnter)
        const request: CancelTransactionRequest = {
            surrogateId: null,
            token: vendorToken,
            maturityPriceId: null,
            maturityGraceId: null,
            firstPaymentDate: null,
        }
        try {
            await mutateCancelTransaction(request)
            void checkoutService.recordStep({
                ...paramsOnExit,
                data: { token: vendorToken, vendorData },
            })
            logoutUser()
            if (vendorData?.returnUrl) {
                if (isLocalEnvironment) {
                    window.location.href = 'http://localhost:4200/dashboard/stores'
                    return
                }
                window.location.href = vendorData.returnUrl
                return
            }
            setIsAuthenticated(false)
            navigate('/', {
                replace: true,
            })
        } catch (error) {
            void checkoutService.recordStep({
                ...paramsOnExit,
                data: { error },
            })
            navigate('/', {
                replace: true,
            })
        }
    }

    return (
        <>
            <div
                className="flex flex-col max-w-[840px] w-full sticky items-center gap-1 max-[766px]:px-2 min-[767px]:px-4 pt-1 rounded-lg max-[766px]:w-full max-[766px]:rounded-none min-[767px]:max-[899px]:rounded-none min-[767px]:max-[899px]:w-full"
                style={{
                    background: 'linear-gradient(0deg, rgba(2,0,36,0) 0%, rgba(243,246,248,1) 58%)',
                }}
            >
                <Dialog onOpenChange={setIsOpen}>
                    {isMarketRom &&
                        showProgressbar &&
                        sessionInfo?.progressBarType === 'SIMPLE' && (
                            <ProgressBar progress={progress} className="w-full" />
                        )}

                    {isMarketSlo &&
                        showProgressbar &&
                        sessionInfo?.progressBarType === 'SIMPLE' && (
                            <ProgressBar progress={progress} className="w-full" />
                        )}

                    <div className="flex justify-between items-center w-full">
                        <div className="z-[1000] flex flex-1">
                            <LanguageSwitcher />
                        </div>
                        <div className="flex flex-1 items-center text-center justify-center">
                            <img
                                src={LpLogoImg}
                                className="w-[126px] h-[37px]"
                                alt="Leanpay Logo"
                            />
                        </div>
                        <div className="flex-1 flex justify-end">
                            <DialogTrigger asChild>
                                <button
                                    onClick={onCancelPress}
                                    className={`cursor-pointer bg-transparent rounded-full ${showCancelIcon && !isOpen ? 'block' : 'hidden'}`}
                                    aria-label={t('common.leave')}
                                >
                                    <CloseCircleIcon />
                                </button>
                            </DialogTrigger>
                        </div>
                    </div>
                    {isMarketSlo &&
                        showProgressbar &&
                        sessionInfo?.progressBarType === 'ENHANCED' && <EnhancedProgressBarSlo />}
                    {isMarketRom &&
                        showProgressbar &&
                        sessionInfo?.progressBarType === 'ENHANCED' && <EnhancedProgressBarRom />}
                    <DialogContent>
                        <div className="p-4 pt-12 self-stretch text-center">
                            <img src={ThinkingFaceImg} alt="Thinking" className="mx-auto" />

                            <p
                                className="text-base mb-20 mt-16"
                                dangerouslySetInnerHTML={{
                                    __html: t('common.cancelTransaction'),
                                }}
                            />

                            <DialogClose asChild>
                                <Button
                                    testId="next"
                                    onClick={handleExitCheckoutProcess}
                                    className="mb-4"
                                >
                                    {t('common.leave')}
                                </Button>
                            </DialogClose>

                            <DialogClose asChild>
                                <Button testId="previous" variant="secondary">
                                    {t('common.continue')}
                                </Button>
                            </DialogClose>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {children}
        </>
    )
}

export default PrequalifyHeader
